/**
 * BasketballCourtHeatMap — SVG basketball court with a colored zone overlay.
 *
 * Renders a realistic mini basketball court (aspect 94:50 ≈ 1.88:1) and paints
 * each of the 24 zones (6 cols × 4 rows) according to the provided heat-map
 * data using a yellow → orange → red colour scale.
 */
import React, { useMemo } from "react";
import {
  ZONES,
  COURT_WIDTH,
  COURT_HEIGHT,
  ZONE_COLS,
  ZONE_ROWS,
  ZONE_W,
  ZONE_H,
  intensityToColor,
  type HeatMapData,
} from "../../utils/heatMapZones";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface BasketballCourtHeatMapProps {
  /** Heat-map data containing counts per zone */
  data: HeatMapData;
  /** Optional title displayed above the court */
  title?: string;
  /** Team colour for the title badge (hex) */
  accentColor?: string;
  /** data-testid for E2E */
  "data-testid"?: string;
}

/* ------------------------------------------------------------------ */
/*  Court markup constants (all in court-coordinate units: 94 × 50)   */
/* ------------------------------------------------------------------ */

// Free-throw lane (paint / key) — NBA regulation
const PAINT_DEPTH = 19;   // depth from baseline to free-throw line (ft)
const PAINT_W = 16;       // width, centered on basket (ft)
const PAINT_Y = (COURT_HEIGHT - PAINT_W) / 2;  // 17

// Three-point line
const THREE_PT_R = 23.75; // arc radius from basket center (ft)
const TP_SIDE = 3;        // straight section offset from sideline (ft)

// Basket position
const BASKET_X = 5.25;    // basket center from baseline (ft)

// Backboard
const BKBD_X = 4;         // backboard face from baseline (ft)
const BKBD_W = 6;         // backboard width (ft)

// Center circle radius
const CC_R = 6;

// Free-throw circle radius
const FT_R = 6;

// Restricted area arc radius
const RA_R = 4;

// Three-point corner intersection (where arc meets the straight section)
const MID_Y = COURT_HEIGHT / 2;
const TP_DY = MID_Y - TP_SIDE;  // 22
const TP_DX = Math.sqrt(THREE_PT_R * THREE_PT_R - TP_DY * TP_DY); // ≈ 8.95
const L_TP_CORNER = BASKET_X + TP_DX;        // ≈ 14.20
const R_TP_CORNER = COURT_WIDTH - BASKET_X - TP_DX; // ≈ 79.80

// Lane hash marks (distance from baseline)
const HASH_MARKS = [7, 8, 11, 14];
const HASH_LEN = 0.6;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const BasketballCourtHeatMap: React.FC<BasketballCourtHeatMapProps> = ({
  data,
  title,
  accentColor = "#6366f1",
  "data-testid": testId,
}) => {
  /* Compute zone colours from data */
  const zoneColors = useMemo(() => {
    if (data.max === 0) {
      return ZONES.map(() => "rgba(0,0,0,0)");
    }
    return ZONES.map((zone) => {
      const count = data.counts[zone.id];
      const intensity = count / data.max;
      return intensityToColor(intensity);
    });
  }, [data]);

  return (
    <div className="flex flex-col items-center gap-1" data-testid={testId}>
      {/* Title badge */}
      {title && (
        <div className="flex items-center gap-2 mb-1">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: accentColor }}
          />
          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
            {title}
          </span>
          <span className="text-xs text-slate-500">
            ({data.total} {data.total === 1 ? "event" : "events"})
          </span>
        </div>
      )}

      {/* SVG Court */}
      <svg
        viewBox={`-2 -2 ${COURT_WIDTH + 4} ${COURT_HEIGHT + 4}`}
        className="w-full max-w-md rounded-lg border border-slate-700 bg-amber-900"
        preserveAspectRatio="xMidYMid meet"
        data-testid={testId ? `${testId}-svg` : undefined}
      >
        {/* ─── Court background (hardwood) ─── */}
        <rect
          x={0}
          y={0}
          width={COURT_WIDTH}
          height={COURT_HEIGHT}
          fill="#b5651d"
          rx={1}
        />

        {/* ─── Alternating plank stripes ─── */}
        {Array.from({ length: ZONE_COLS }, (_, i) =>
          i % 2 === 0 ? (
            <rect
              key={`stripe-${i}`}
              x={i * ZONE_W}
              y={0}
              width={ZONE_W}
              height={COURT_HEIGHT}
              fill="rgba(255,255,255,0.03)"
            />
          ) : null,
        )}

        {/* ─── Court markings ─── */}
        <g stroke="rgba(255,255,255,0.55)" strokeWidth={0.4} fill="none">
          {/* Court boundary */}
          <rect
            x={0}
            y={0}
            width={COURT_WIDTH}
            height={COURT_HEIGHT}
            rx={0.5}
          />

          {/* Half-court line */}
          <line
            x1={COURT_WIDTH / 2}
            y1={0}
            x2={COURT_WIDTH / 2}
            y2={COURT_HEIGHT}
          />

          {/* Center circle */}
          <circle cx={COURT_WIDTH / 2} cy={COURT_HEIGHT / 2} r={CC_R} />

          {/* Center spot */}
          <circle
            cx={COURT_WIDTH / 2}
            cy={COURT_HEIGHT / 2}
            r={0.5}
            fill="rgba(255,255,255,0.55)"
          />

          {/* ── Left half ── */}

          {/* Paint (key) */}
          <rect x={0} y={PAINT_Y} width={PAINT_DEPTH} height={PAINT_W} />

          {/* Free-throw circle — solid exterior half */}
          <path d={`M ${PAINT_DEPTH} ${MID_Y - FT_R} A ${FT_R} ${FT_R} 0 0 1 ${PAINT_DEPTH} ${MID_Y + FT_R}`} />
          {/* Free-throw circle — dashed interior half */}
          <path d={`M ${PAINT_DEPTH} ${MID_Y + FT_R} A ${FT_R} ${FT_R} 0 0 1 ${PAINT_DEPTH} ${MID_Y - FT_R}`} strokeDasharray="1.2 0.8" />

          {/* Three-point line (corners + arc) */}
          <path d={`M 0 ${TP_SIDE} L ${L_TP_CORNER.toFixed(2)} ${TP_SIDE} A ${THREE_PT_R} ${THREE_PT_R} 0 0 1 ${L_TP_CORNER.toFixed(2)} ${COURT_HEIGHT - TP_SIDE} L 0 ${COURT_HEIGHT - TP_SIDE}`} />

          {/* Restricted area arc */}
          <path d={`M 0 ${MID_Y - RA_R} L ${BASKET_X} ${MID_Y - RA_R} A ${RA_R} ${RA_R} 0 0 1 ${BASKET_X} ${MID_Y + RA_R} L 0 ${MID_Y + RA_R}`} />

          {/* Backboard */}
          <line x1={BKBD_X} y1={MID_Y - BKBD_W / 2} x2={BKBD_X} y2={MID_Y + BKBD_W / 2} strokeWidth={0.6} />

          {/* Basket rim */}
          <circle cx={BASKET_X} cy={MID_Y} r={0.75} />

          {/* Lane hash marks — left */}
          {HASH_MARKS.map((d) => (
            <React.Fragment key={`lh-${d}`}>
              <line x1={d} y1={PAINT_Y} x2={d} y2={PAINT_Y - HASH_LEN} />
              <line x1={d} y1={PAINT_Y + PAINT_W} x2={d} y2={PAINT_Y + PAINT_W + HASH_LEN} />
            </React.Fragment>
          ))}

          {/* ── Right half (mirrored) ── */}

          {/* Paint (key) */}
          <rect x={COURT_WIDTH - PAINT_DEPTH} y={PAINT_Y} width={PAINT_DEPTH} height={PAINT_W} />

          {/* Free-throw circle — solid exterior half */}
          <path d={`M ${COURT_WIDTH - PAINT_DEPTH} ${MID_Y - FT_R} A ${FT_R} ${FT_R} 0 0 0 ${COURT_WIDTH - PAINT_DEPTH} ${MID_Y + FT_R}`} />
          {/* Free-throw circle — dashed interior half */}
          <path d={`M ${COURT_WIDTH - PAINT_DEPTH} ${MID_Y + FT_R} A ${FT_R} ${FT_R} 0 0 0 ${COURT_WIDTH - PAINT_DEPTH} ${MID_Y - FT_R}`} strokeDasharray="1.2 0.8" />

          {/* Three-point line (corners + arc) */}
          <path d={`M ${COURT_WIDTH} ${TP_SIDE} L ${R_TP_CORNER.toFixed(2)} ${TP_SIDE} A ${THREE_PT_R} ${THREE_PT_R} 0 0 0 ${R_TP_CORNER.toFixed(2)} ${COURT_HEIGHT - TP_SIDE} L ${COURT_WIDTH} ${COURT_HEIGHT - TP_SIDE}`} />

          {/* Restricted area arc */}
          <path d={`M ${COURT_WIDTH} ${MID_Y - RA_R} L ${COURT_WIDTH - BASKET_X} ${MID_Y - RA_R} A ${RA_R} ${RA_R} 0 0 0 ${COURT_WIDTH - BASKET_X} ${MID_Y + RA_R} L ${COURT_WIDTH} ${MID_Y + RA_R}`} />

          {/* Backboard */}
          <line x1={COURT_WIDTH - BKBD_X} y1={MID_Y - BKBD_W / 2} x2={COURT_WIDTH - BKBD_X} y2={MID_Y + BKBD_W / 2} strokeWidth={0.6} />

          {/* Basket rim */}
          <circle cx={COURT_WIDTH - BASKET_X} cy={MID_Y} r={0.75} />

          {/* Lane hash marks — right */}
          {HASH_MARKS.map((d) => (
            <React.Fragment key={`rh-${d}`}>
              <line x1={COURT_WIDTH - d} y1={PAINT_Y} x2={COURT_WIDTH - d} y2={PAINT_Y - HASH_LEN} />
              <line x1={COURT_WIDTH - d} y1={PAINT_Y + PAINT_W} x2={COURT_WIDTH - d} y2={PAINT_Y + PAINT_W + HASH_LEN} />
            </React.Fragment>
          ))}
        </g>

        {/* ─── Heat-map zone overlay ─── */}
        <g data-testid={testId ? `${testId}-zones` : undefined}>
          {ZONES.map((zone) => (
            <rect
              key={zone.id}
              x={zone.x0}
              y={zone.y0}
              width={ZONE_W}
              height={ZONE_H}
              fill={zoneColors[zone.id]}
              data-testid={testId ? `${testId}-zone-${zone.id}` : undefined}
              data-zone-id={zone.id}
              data-zone-count={data.counts[zone.id]}
            >
              <title>
                {zone.label}: {data.counts[zone.id]} event
                {data.counts[zone.id] !== 1 ? "s" : ""}
              </title>
            </rect>
          ))}
        </g>

        {/* ─── Zone grid lines (subtle) ─── */}
        <g stroke="rgba(255,255,255,0.12)" strokeWidth={0.2} fill="none">
          {Array.from({ length: ZONE_COLS - 1 }, (_, i) => (
            <line
              key={`vcol-${i}`}
              x1={(i + 1) * ZONE_W}
              y1={0}
              x2={(i + 1) * ZONE_W}
              y2={COURT_HEIGHT}
            />
          ))}
          {Array.from({ length: ZONE_ROWS - 1 }, (_, i) => (
            <line
              key={`hrow-${i}`}
              x1={0}
              y1={(i + 1) * ZONE_H}
              x2={COURT_WIDTH}
              y2={(i + 1) * ZONE_H}
            />
          ))}
        </g>

        {/* ─── Zone counts (only when > 0) ─── */}
        <g
          fontSize={4}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {ZONES.map((zone) =>
            data.counts[zone.id] > 0 ? (
              <text
                key={`label-${zone.id}`}
                x={zone.x0 + ZONE_W / 2}
                y={zone.y0 + ZONE_H / 2}
                style={{
                  textShadow: "0 0 3px rgba(0,0,0,0.8)",
                  pointerEvents: "none",
                }}
              >
                {data.counts[zone.id]}
              </text>
            ) : null,
          )}
        </g>
      </svg>
    </div>
  );
};

export default BasketballCourtHeatMap;
